// frontend/src/components/Clinics/Clinics.js

import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import './Clinics.css';

const EMPTY_FORM = {
  clinic_name: '',
  journal_system: '', // <-- default (ingen överföring)
  use_internal_journal: false, // Aktiverar inbyggt journalsystem
  has_notes_integration: false, // Hämtar mallar + diktering från Aion Notes
  has_patient_portal: false, // Patientmodul: formulär till patienten via Bokningsappen
  patient_portal_url: '', // Bas-URL för notislänkar (t.ex. https://apexterapi.aionbookings.se)
  sms_sender: '', // SMS-avsändarnamn (max 11 tecken)
  has_spar_lookup: false, // SPAR-uppslag (folkbokföring) — kräver eget SPAR-beslut
  spar_kundnr_slutkund: '', // Kundnummer slutkund från SPAR
  spar_uppdrag_id: '', // Uppdrags-id från SPAR
};

function Clinics() {
  const [clinics, setClinics] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  // SPAR-klientcertifikatets status (varning 30 dagar före utgång)
  const [sparStatus, setSparStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editClinicId, setEditClinicId] = useState(null);

  useEffect(() => {
    fetchClinics();
    apiClient.get('/api/spar/status')
      .then(resp => setSparStatus(resp.data))
      .catch(() => setSparStatus(null));
  }, []);

  const fetchClinics = async () => {
    try {
      const response = await apiClient.get('/api/clinics');
      setClinics(response.data);
    } catch (error) {
      console.error('Error fetching clinics:', error);
      alert('Ett fel uppstod vid hämtning av kliniker.');
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name.trim()) {
      alert('Klinikens namn får inte vara tomt.');
      return;
    }

    try {
      if (isEditing) {
        // Vid uppdatering
        await apiClient.put(`/api/clinics/${editClinicId}`, form);
        alert('Klinik uppdaterad.');
      } else {
        // Skapa ny klinik
        await apiClient.post('/api/clinics', form);
        alert('Klinik skapad.');
      }
      // Rensa formuläret
      setForm(EMPTY_FORM);
      setIsEditing(false);
      setEditClinicId(null);
      // Hämta uppdaterad lista
      fetchClinics();
    } catch (error) {
      console.error('Error saving clinic:', error);
      alert(error.response?.data?.msg || 'Ett fel uppstod vid sparande av klinik.');
    }
  };

  const handleEdit = (clinic) => {
    // När vi klickar "Redigera" på en rad, sätt formet
    setForm({
      clinic_name: clinic.clinic_name,
      journal_system: clinic.journal_system || '',
      use_internal_journal: clinic.use_internal_journal || false,
      has_notes_integration: clinic.has_notes_integration || false,
      has_patient_portal: clinic.has_patient_portal || false,
      patient_portal_url: clinic.patient_portal_url || '',
      sms_sender: clinic.sms_sender || '',
      has_spar_lookup: clinic.has_spar_lookup || false,
      spar_kundnr_slutkund: clinic.spar_kundnr_slutkund || '',
      spar_uppdrag_id: clinic.spar_uppdrag_id || '',
    });
    setIsEditing(true);
    setEditClinicId(clinic.clinic_id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill ta bort denna klinik?')) {
      try {
        await apiClient.delete(`/api/clinics/${id}`);
        alert('Klinik borttagen.');
        fetchClinics();
      } catch (error) {
        console.error('Error deleting clinic:', error);
        alert('Ett fel uppstod vid borttagning av klinik.');
      }
    }
  };

  const handleCancelEdit = () => {
    setForm(EMPTY_FORM);
    setIsEditing(false);
    setEditClinicId(null);
  };

  return (
    <div className="clinics-container">
      <h2>Klinikhantering</h2>

      {/* Globala systeminställningar */}
      {sparStatus?.certificate?.configured && (
        <div className="system-settings-section">
          <h3>Systeminställningar</h3>
          <div className="system-setting-row">
            <label>SPAR-certifikat:</label>
            {sparStatus.certificate.warning ? (
              <span className="status-warning">
                <i className="fas fa-exclamation-triangle"></i>{' '}
                {sparStatus.certificate.expires_at
                  ? (sparStatus.certificate.days_left < 0
                    ? `Gick ut ${sparStatus.certificate.expires_at} — SPAR-uppslag fungerar inte`
                    : `Går ut ${sparStatus.certificate.expires_at} (${sparStatus.certificate.days_left} dagar kvar) — förnya hos Expisoft`)
                  : 'Giltighetstiden kunde inte läsas — kontrollera certifikatet på servern'}
              </span>
            ) : (
              <span>Giltigt till {sparStatus.certificate.expires_at}</span>
            )}
          </div>
        </div>
      )}

      {/* Formulär för att skapa/uppdatera kliniker */}
      <form onSubmit={handleSubmit} className="clinic-form">
        <input
            type="text"
            name="clinic_name"
            placeholder="Namn på klinik"
            value={form.clinic_name}
            onChange={handleFormChange}
            required
            className="clinic-input"
          />

        {/* CHECKBOX FÖR INBYGGT JOURNALSYSTEM - Flyttad upp för logiskt flöde */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="use_internal_journal"
            checked={form.use_internal_journal}
            onChange={handleFormChange}
          />
          Använd inbyggt journalsystem
        </label>
        {/* CHECKBOX FÖR NOTES-INTEGRATION (mallar + diktering från Aion Notes) */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="has_notes_integration"
            checked={form.has_notes_integration}
            onChange={handleFormChange}
          />
          Notes-integration (mallar &amp; diktering från Aion Notes)
        </label>
        {/* PATIENTMODUL: formulär till patienten via Bokningsappen */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="has_patient_portal"
            checked={form.has_patient_portal}
            onChange={handleFormChange}
          />
          Patientmodul (formulär till patienten via Bokningsappen)
        </label>
        {form.has_patient_portal && (
          <input
            type="url"
            name="patient_portal_url"
            placeholder="Bokningsappens bas-URL, t.ex. https://apexterapi.aionbookings.se (tomt = dev-standard)"
            value={form.patient_portal_url}
            onChange={handleFormChange}
            className="clinic-input"
          />
        )}
        {/* SPAR: folkbokföringsuppslag (eget SPAR-beslut per klinik) */}
        <label className="clinic-checkbox-label">
          <input
            type="checkbox"
            name="has_spar_lookup"
            checked={form.has_spar_lookup}
            onChange={handleFormChange}
          />
          SPAR-uppslag (namn och adress från folkbokföringen)
        </label>
        {form.has_spar_lookup && (
          <div className="clinic-spar-fields">
            <input
              type="text"
              name="spar_kundnr_slutkund"
              placeholder="SPAR kundnummer slutkund"
              value={form.spar_kundnr_slutkund}
              onChange={handleFormChange}
              inputMode="numeric"
              className="clinic-input"
            />
            <input
              type="text"
              name="spar_uppdrag_id"
              placeholder="SPAR uppdrags-id"
              value={form.spar_uppdrag_id}
              onChange={handleFormChange}
              inputMode="numeric"
              className="clinic-input"
            />
          </div>
        )}
        {/* DROP-DOWN FÖR EXTERNT JOURNALSYSTEM (överföring) */}
        <label className="clinic-select-label">Journalsystem för överföring:</label>
        <select
          name="journal_system"
          value={form.journal_system}
          onChange={handleFormChange}
          className="clinic-select"
        >
          <option value="">Ingen</option>
          <option value="Kaddio">Kaddio</option>
          <option value="Clinicbuddy">Clinicbuddy</option>
          <option value="Webdoc">Webdoc</option>
          <option value="PMO">PMO</option>
          <option value="TakeCare">TakeCare</option>
          <option value="SLS_PDF">SLS_PDF</option>
        </select>

        {/* SMS-AVSÄNDARE */}
        <div className="clinic-sms-sender">
          <label>SMS-avsändare:</label>
          <input
            type="text"
            name="sms_sender"
            placeholder="t.ex. Kliniken"
            value={form.sms_sender}
            onChange={handleFormChange}
            maxLength={11}
            className="clinic-input sms-input"
          />
          <span className="sms-hint">Max 11 tecken, visas som avsändare vid SMS</span>
        </div>

        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
          <button type="submit" style={{ flex: 1 }}>
            {isEditing ? 'Uppdatera Klinik' : 'Skapa Klinik'}
          </button>

          {isEditing && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="cancel-button"
              style={{ flex: 0 }}
            >
              Avbryt
            </button>
          )}
        </div>
      </form>

      {/* Lista med kliniker */}
      <table className="clinic-table">
        <thead>
          <tr>
            <th>Klinik-ID</th>
            <th>Kliniknamn</th>
            <th>Journalsystem</th>
            <th>Inbyggt journal</th>
            <th>Notes-integration</th>
            <th>SPAR</th>
            <th>SMS-avsändare</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {clinics.map((clinic) => (
            <tr key={clinic.clinic_id}>
              <td>{clinic.clinic_id}</td>
              <td>{clinic.clinic_name}</td>
              <td className="clinic-journal-system">
                {clinic.journal_system || <span className="status-inactive">-</span>}
              </td>
              <td className="clinic-journal-status">
                {clinic.use_internal_journal ? (
                  <span className="status-active">Aktivt</span>
                ) : (
                  <span className="status-inactive">-</span>
                )}
              </td>
              <td className="clinic-notes-status">
                {clinic.has_notes_integration ? (
                  <span className="status-active">Aktivt</span>
                ) : (
                  <span className="status-inactive">-</span>
                )}
              </td>
              <td className="clinic-spar-status">
                {clinic.has_spar_lookup ? (
                  <span className="status-active">Aktivt</span>
                ) : (
                  <span className="status-inactive">-</span>
                )}
              </td>
              <td className="clinic-sms-status">
                {clinic.sms_sender || <span className="status-inactive">-</span>}
              </td>
              <td>
                <button onClick={() => handleEdit(clinic)}>Redigera</button>
                <button
                  onClick={() => handleDelete(clinic.clinic_id)}
                  className="delete-button"
                >
                  Ta Bort
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Clinics;
